import * as Yup from 'yup'

import { HomeQueries } from '@/graphql/queries/home.queries'
import reservationQueries from '@/graphql/queries/reservation.queries'
import { TicketQueries } from '@/graphql/queries/ticket.queries'
import { UserQueries } from '@/graphql/queries/user.queries'
import { graphQLClient } from '@/hooks/useGraphql'
import { Origin, Type } from '@/types/Ticket'

export const searchHome = (inputValue: string) => {
  return graphQLClient.request<any>(HomeQueries.searchHome(), { searchString: inputValue }).then((homeRes) => {
    return homeRes.homes.map((home) => ({ label: home?.searchDocument || '', value: home?.id }))
  })
}

export const searchReservation = (inputValue: string) => {
  return graphQLClient
    .request<any>(reservationQueries.searchReservation, { filter: { limit: 20 }, searchString: inputValue })
    .then((resRespons) => {
      return resRespons.reservations.map((res) => {
        const _label = res?.id + ' - ' + res?.home?.searchDocument + ''
        return { label: _label.substring(0, 75), value: res?.id }
      })
    })
}

export const searchReservationByUser = (userId: number, inputValue: string) => {
  return graphQLClient
    .request<any>(reservationQueries.searchReservation, { filter: { limit: 20, userId }, searchString: inputValue })
    .then((res) => {
      return res.reservations.map((r) => {
        const label = r?.id + ' - ' + r?.home?.searchDocument + ''
        return {
          label: label.substring(0, 75),
          value: r?.id,
          homeId: r?.home?.id,
          homeLabel: r?.home?.searchDocument || '',
          status: r?.status,
        }
      })
    })
}

export const searchMember = (inputValue: string) => {
  return graphQLClient
    .request<any>(UserQueries.getSearchedMember(), { searchString: inputValue })
    .then((resRespons) => {
      return resRespons.members.map((member) => ({ label: member?.fullName || '', value: member?.id }))
    })
}

export const searchParentTicket = (inputValue: string) => {
  return graphQLClient
    .request<any>(TicketQueries.searchParentTickets(), {
      filter: { search: inputValue, isRoot: true, allowAsParent: true },
    })
    .then((res) => {
      return res.tickets.map((ticket) => ({
        label: `#${ticket.id} - ${ticket.subject}`,
        value: ticket.id,
      }))
    })
}

export const searchOwner = (inputValue: string) => {
  return graphQLClient
    .request<any>(UserQueries.getSearchOwner(), {
      searchString: inputValue,
    })
    .then((resRespons) => {
      return resRespons.authorizedUsers.map((authorizedUser) => ({
        label: authorizedUser?.fullName || '',
        value: authorizedUser?.id,
      }))
    })
}

export const initialValues = {
  ticketTypeId: '',
  ticketType: '',
  ticketReasonId: '',
  ticketHomeAreaId: undefined,
  description: '',
  parentTicketId: undefined,
  permissionToEnter: false,
  origin: Origin.MANUAL,
}

export const formSchema = Yup.object().shape(
  {
    ticketTypeId: Yup.string().required('Please select Ticket Type'),
    ticketReasonId: Yup.string().when('ticketType', {
      is: (ticketType: Type) => ticketType !== Type.CONVERSATION,
      then: () => Yup.string().required('Please select Ticket Reason'),
    }),
    description: Yup.string().required('Please add some description'),
    homeId: Yup.string().when(['contactId', 'reservationId', 'ticketType'], {
      is: (contactId, reservationId, ticketType) => !contactId && !reservationId && ticketType !== Type.CONVERSATION,
      then: () => Yup.string().required('Please select Home, Member or Reservation'),
    }),
    contactId: Yup.string().when(['homeId', 'reservationId', 'ticketType'], {
      is: (homeId, reservationId, ticketType) => !homeId && !reservationId && ticketType !== Type.CONVERSATION,
      then: () => Yup.string().required('Please select Home, Member or Reservation'),
    }),
    reservationId: Yup.string().when(['contactId', 'homeId', 'ticketType'], {
      is: (contactId, homeId, ticketType) => !contactId && !homeId && ticketType !== Type.CONVERSATION,
      then: () => Yup.string().required('Please select Home, Member or Reservation'),
    }),
    ticketHomeAreaId: Yup.string().when(['ticketType'], {
      is: (ticketType: Type) => ticketType === Type.MAINTENANCE,
      then: () => Yup.string().required('Please select Home Area'),
    }),
  },
  [
    ['homeId', 'reservationId'],
    ['homeId', 'ticketType'],
    ['contactId', 'homeId'],
    ['contactId', 'reservationId'],
    ['contactId', 'ticketType'],
    ['reservationId', 'ticketType'],
  ],
)

export type Option = {
  label: string
  value?: number | string
}
export type Options = Array<Option>

type InitialFormValues = typeof initialValues

export interface FormValuesType extends Omit<InitialFormValues, 'ticketHomeAreaId' | 'parentTicketId'> {
  attachments?: []
  contactId?: number
  homeId?: number
  parentTicketId?: number
  reservationId?: number
  ticketHomeAreaId?: number
}
